import { FSWatcher } from "fs";
import path from "path";

import { watch } from "chokidar";
import _ from "lodash";
import pLimit from "p-limit";

import { execCommandAsync } from "../../Commons/ExecUtils";
import { buildActionBegin, ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { ObservableValueProducerBase } from "../../Commons/TasksCore/ChainableValueProducer";
import { FormGenerator } from "../Form/GenerateForm";
import { ProcessUtils } from "../ProcessUtils/ProcessUtils";
import { ShellProcessHandle } from "../ProcessUtils/ShellProcessHandle";
import { defaultRegistry } from "../../Commons/Defaults";

import { createAsyncGenerator } from "./GeneratorWorker/CreateAsyncGenerator";

const generatorBuildLimit = pLimit(1);

export class BuildGeneratorFromSourcesTask extends ObservableValueProducerBase<FormGenerator> {
    private readonly pathToEngineSources: string;
    private readonly logger: ILogger;
    private useWatch = false;
    private currentGenerator?: FormGenerator;
    private buildProcess?: ShellProcessHandle;
    private buildResultWatcher?: FSWatcher;

    public constructor(pathToEngineSources: string, logger: ILogger = getLogger()) {
        super();
        this.logger = logger.withPrefix("GENERATOR");
        this.pathToEngineSources = pathToEngineSources;
    }

    public async getCurrentValue(): Promise<FormGenerator> {
        return generatorBuildLimit(async () => {
            if (this.currentGenerator != undefined) {
                return this.currentGenerator;
            }
            if (this.useWatch) {
                if (this.buildProcess == undefined) {
                    this.buildProcess = await this.runWatchProcessStarted();
                    this.buildResultWatcher = await this.createBuildResultWatcher(
                        path.join(this.pathToEngineSources, "Generator", "dist")
                    );
                    this.currentGenerator = createAsyncGenerator(path.join(this.pathToEngineSources, "Generator"));
                }
            } else {
                this.currentGenerator = await this.acquireGeneratorFromSources();
            }
            if (this.currentGenerator == undefined) {
                throw new Error("InvalidProgramState");
            }
            return this.currentGenerator;
        });
    }

    protected async beginListening(): Promise<void> {
        this.useWatch = true;
    }

    protected async stopListening(): Promise<void> {
        if (this.buildProcess != undefined) {
            try {
                const buildProcess = this.buildProcess;
                this.buildProcess = undefined;
                await buildProcess.kill();
            } catch (error) {
                this.logger.error(`Failed to stop process: ${error}`);
            }
        }
        if (this.buildResultWatcher != undefined) {
            this.buildResultWatcher.close();
        }
        await this.currentGenerator?.dispose?.();
        this.useWatch = false;
    }

    private readonly handleUpdate = async (): Promise<void> => {
        this.logger.log("Generator sources updated");
        await this.currentGenerator?.dispose?.();
        this.currentGenerator = createAsyncGenerator(path.join(this.pathToEngineSources, "Generator"));
        await this.emitUpdate(this.currentGenerator);
    };

    private async acquireGeneratorFromSources(): Promise<FormGenerator> {
        const action = buildActionBegin(this.logger, "Build generator");
        const buildDirectory = await this.getBuildDirectory();
        await execCommandAsync(this.logger, `npm run generator:build`, {
            cwd: buildDirectory,
            env: {
                ...process.env,
                NPM_CONFIG_REGISTRY: defaultRegistry,
            },
        });
        const result = createAsyncGenerator(path.join(this.pathToEngineSources, "Generator"));
        action.complete();
        return result;
    }

    private async getBuildDirectory(): Promise<string> {
        return this.pathToEngineSources;
    }

    private async runWatchProcessStarted(): Promise<ShellProcessHandle> {
        this.logger.log("Start watch");
        const buildDirectory = await this.getBuildDirectory();
        const result = ProcessUtils.createShellProcess(
            "npm",
            ["run", "generator:build", "--", "--watch"],
            buildDirectory
        );
        result.onStdOut.subscribe(data => {
            this.logger.verbose(data.toString());
        });
        result.onStdErr.subscribe(data => {
            this.logger.verbose(data.toString());
        });
        result.start();
        await this.waitFirstCompilationCompleted(result);
        this.logger.log("Successfully complied");
        return result;
    }

    private async waitFirstCompilationCompleted(result: ShellProcessHandle): Promise<void> {
        await result.waitOutputIncludesString("Successfully compiled");
    }

    private async createBuildResultWatcher(pathToGeneratorOutputDir: string): Promise<FSWatcher> {
        const maskToObserveGeneratorChanges = ["Generator/src/**/*.js", "index.js"];
        const watcher = watch(maskToObserveGeneratorChanges, {
            ignoreInitial: true,
            cwd: pathToGeneratorOutputDir,
            ignorePermissionErrors: false,
            usePolling: true,
            awaitWriteFinish: { stabilityThreshold: 200, pollInterval: 50 },
        });
        const handleAnyFsEventDebounced = _.debounce(() => this.handleUpdate(), 500);
        watcher.on("all", handleAnyFsEventDebounced);
        watcher.on("error", handleAnyFsEventDebounced);
        // @ts-ignore
        return watcher;
    }
}
